import type { UseMutationOptions } from '@tanstack/react-query';
import { useMutation } from '@tanstack/react-query';
import type { CheckoutSession } from 'models/checkoutSession';
import type { RecurringOffer } from 'models/offer';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { getOrder } from 'selectors';
import { getAccessToken } from 'selectors/entities/authentication/authentication';
import {
  getCurrentLocale,
  getFieldValues,
  getSelectedOffer,
} from 'selectors/ui/ui';
import {
  type CheckoutSessionType,
  type CreateSessionParams,
  type DirectDebitProcessor,
  type MandateAch,
  sessions,
} from 'services/sessions/sessions';

const PROCESSORS_REQUIRING_MANDATE_ACH: DirectDebitProcessor[] = ['CRBA'];

type MutateFnArgs = {
  paymentMethodToken?: string;
  recurringReceivableId?: string;
  captchaResponse?: string;
};

type SessionData = {
  sessionType: CheckoutSessionType;
  orderId?: string;
  orderToken?: string;
  recurringReceivableId?: string;
};

type UseCreateCheckoutSessionProps = {
  createSessionOnMount?: boolean;
  sessionData: SessionData;
  options?: UseMutationOptions<CheckoutSession, Error, MutateFnArgs>;
};

const useCreateCheckoutSession = ({
  createSessionOnMount = true,
  sessionData,
  options,
}: UseCreateCheckoutSessionProps) => {
  const sessionConfig = useSessionConfiguration(sessionData);
  const mandateAch = useMandateCalculation(sessionData.sessionType);

  const mutation = useMutation(
    ['createCheckoutSession', sessionData.sessionType],
    async ({ paymentMethodToken, captchaResponse }: MutateFnArgs = {}) => {
      const params: CreateSessionParams = {
        ...sessionConfig,
        paymentMethodToken,
        mandateAch,
        captchaResponse,
      };

      return sessions.create(params);
    },
    options,
  );

  const { mutate } = mutation;

  useEffect(() => {
    if (createSessionOnMount) {
      mutate({});
    }
  }, [createSessionOnMount, mutate]);

  return {
    ...mutation,
    sessionId: mutation.data?.id || '',
  };
};

const useSessionConfiguration = (sessionData: SessionData) => {
  const accessToken = useSelector(getAccessToken);
  const locale = useSelector(getCurrentLocale);
  const order = useSelector(getOrder);
  const { session_id: consumerSessionId } = useSelector(getFieldValues);

  return {
    accessToken,
    locale,
    sessionType: sessionData.sessionType,
    orderId: sessionData.orderId ?? order?.id,
    orderToken: sessionData.orderToken ?? order?.token,
    recurringReceivableId: sessionData.recurringReceivableId,
    consumerSessionId: consumerSessionId as string | undefined,
  };
};

const useMandateCalculation = (
  sessionType: CheckoutSessionType,
): MandateAch | undefined => {
  const offer = useSelector(getSelectedOffer) as RecurringOffer | undefined;

  if (sessionType !== 'direct_debit_tokenization_for_installment') {
    return undefined;
  }

  if (!offer || !offer.installments?.length) {
    return undefined;
  }

  const processor = offer.paymentMethodProcessor as DirectDebitProcessor;

  if (!PROCESSORS_REQUIRING_MANDATE_ACH.includes(processor)) {
    return undefined;
  }

  const installments = offer.installments;
  return {
    initDate: installments[0].date,
    finalDate: installments[installments.length - 1].date,
    numberOfPayments: installments.length,
  };
};

export { useCreateCheckoutSession };
export type { UseCreateCheckoutSessionProps };
